import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { typography } from "components/design/typography";
import {
  ButtonSize,
  ButtonUsage,
  ButtonVariant,
} from "components/atoms/Button";

interface ButtonWrapperProps {
  color: ButtonUsage;
  size: ButtonSize;
  variant: ButtonVariant;
  disabledColor?: boolean;
  left?: boolean;
  right?: boolean;
}

const ButtonWrapper = styled.button<ButtonWrapperProps>`
  height: ${({ size }) => {
    const heightMap = {
      Large: "48px",
      Medium: "40px",
      Small: "32px",
      XSmall: "16px",
    };

    return heightMap[size] || "";
  }};
  background-color: ${({ color, variant }) => {
    const backgroundColorMap = {
      Contained: {
        Primary: COLOR_SYSTEM.get("Blue")?.[400],
        Secondary: COLOR_SYSTEM.get("Gray")?.[700],
        Tertiary: COLOR_SYSTEM.get("Gray")?.[400],
        Quaternary: COLOR_SYSTEM.get("Gray")?.[200],
        Negative: COLOR_SYSTEM.get("Red")?.[300],
      },
      Tinted: {
        Primary: COLOR_SYSTEM.get("Blue")?.[10],
        Secondary: COLOR_SYSTEM.get("Gray")?.[20],
        Tertiary: COLOR_SYSTEM.get("Gray")?.[20],
        Quaternary: COLOR_SYSTEM.get("Gray")?.[10],
        Negative: COLOR_SYSTEM.get("Red")?.[10],
      },
      Outlined: {
        Primary: COLOR_SYSTEM.get("Gray")?.[0],
        Secondary: COLOR_SYSTEM.get("Gray")?.[0],
        Tertiary: COLOR_SYSTEM.get("Gray")?.[0],
        Quaternary: COLOR_SYSTEM.get("Gray")?.[0],
        Negative: COLOR_SYSTEM.get("Gray")?.[0],
      },
      Ghost: {
        Primary: "transparent",
        Secondary: "transparent",
        Tertiary: "transparent",
        Quaternary: "transparent",
        Negative: "transparent",
      },
    };

    return backgroundColorMap[variant][color];
  }};
  ${({ size }) => {
    const fontMap = {
      Large: typography.get("InputLarge"),
      Medium: typography.get("InputMedium"),
      Small: typography.get("InputSmall"),
      XSmall: typography.get("InputXSmall"),
    };

    return fontMap[size];
  }};
  color: ${({ color, variant }) => {
    const colorMap = {
      Contained: COLOR_SYSTEM.get("Gray")?.[0],
      Primary: COLOR_SYSTEM.get("Blue")?.[400],
      Secondary: COLOR_SYSTEM.get("Gray")?.[700],
      Tertiary: COLOR_SYSTEM.get("Gray")?.[400],
      Quaternary: COLOR_SYSTEM.get("Gray")?.[200],
      Negative: COLOR_SYSTEM.get("Red")?.[300],
    };

    return variant === "Contained" ? colorMap[variant] : colorMap[color];
  }};

  // Border
  border: ${({ color, variant, size }) => {
    if (variant !== "Outlined") return "none";

    const borderMap = {
      Primary: {
        Large: `1px solid ${COLOR_SYSTEM.get("Blue")?.[400]}`,
        Medium: `1px solid ${COLOR_SYSTEM.get("Blue")?.[400]}`,
        Small: `1px solid ${COLOR_SYSTEM.get("Blue")?.[400]}`,
        XSmall: `0.5px solid ${COLOR_SYSTEM.get("Blue")?.[400]}`,
      },
      Secondary: {
        Large: `1px solid ${COLOR_SYSTEM.get("Gray")?.[50]}`,
        Medium: `1px solid ${COLOR_SYSTEM.get("Gray")?.[50]}`,
        Small: `1px solid ${COLOR_SYSTEM.get("Gray")?.[50]}`,
        XSmall: `0.5px solid ${COLOR_SYSTEM.get("Gray")?.[50]}`,
      },
      Tertiary: {
        Large: `1px solid ${COLOR_SYSTEM.get("Gray")?.[50]}`,
        Medium: `1px solid ${COLOR_SYSTEM.get("Gray")?.[50]}`,
        Small: `1px solid ${COLOR_SYSTEM.get("Gray")?.[50]}`,
        XSmall: `0.5px solid ${COLOR_SYSTEM.get("Gray")?.[50]}`,
      },
      Quaternary: {
        Large: `1px solid ${COLOR_SYSTEM.get("Gray")?.[10]}`,
        Medium: `1px solid ${COLOR_SYSTEM.get("Gray")?.[10]}`,
        Small: `1px solid ${COLOR_SYSTEM.get("Gray")?.[10]}`,
        XSmall: `0.5px solid ${COLOR_SYSTEM.get("Gray")?.[10]}`,
      },
      Negative: {
        Large: `1px solid ${COLOR_SYSTEM.get("Red")?.[300]}`,
        Medium: `1px solid ${COLOR_SYSTEM.get("Red")?.[300]}`,
        Small: `1px solid ${COLOR_SYSTEM.get("Red")?.[300]}`,
        XSmall: `0.5px solid ${COLOR_SYSTEM.get("Red")?.[300]}`,
      },
    };

    return borderMap[color][size];
  }};
  border-radius: ${({ size }) => {
    const borderRadiusMap = {
      Large: "8px",
      Medium: "8px",
      Small: "4px",
      XSmall: "2px",
    };

    return borderRadiusMap[size];
  }};

  display: inline-flex;
  justify-content: center;
  align-items: center;
  opacity: ${({ disabledColor }) => {
    if (disabledColor) return "0.3";
  }};
  position: relative;
  overflow: hidden !important;
  cursor: pointer;

  /* &:active {
    ${({ disabledColor }) => {
    if (!disabledColor) return "background-color: #00000026";
  }}
  } */

  .ripple {
    display: block;
    position: absolute;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.3);
    animation: 1s ripple linear;
    transform: scale(0);
  }

  @keyframes ripple {
    to {
      opacity: 0;
      transform: scale(8);
    }
  }
`;

export const IconButtonWrapper = styled(ButtonWrapper)`
  padding: ${({ size }) => {
    const paddingMap = {
      Large: "12px",
      Medium: "10px",
      Small: "8px",
      XSmall: "2px",
    };

    return paddingMap[size];
  }};
`;

export const TextButtonWrapper = styled(ButtonWrapper)`
  padding: ${({ size }) => {
    const paddingMap = {
      Large: "11.5px 20px",
      Medium: "8.5px 16px",
      Small: "5.5px 12px",
      XSmall: "1px 4px",
    };

    return paddingMap[size];
  }};
`;

export const IconWithTextButtonWrapper = styled(ButtonWrapper)`
  padding: ${({ size, left, right }) => {
    const iconPos = left ? "left" : "right";
    const paddingMap = {
      left: {
        Large: "11.5px 20px 11.5px 12px",
        Medium: "8.5px 16px 8.5px 10px",
        Small: "5.5px 12px 5.5px 8px",
        XSmall: "1px 4px 1px 2px",
      },
      right: {
        Large: "11.5px 16px 11.5px 20px",
        Medium: "8.5px 10px 8.5px 16px",
        Small: "5.5px 8px 5.5px 12px",
        XSmall: "1px 2px 1px 4px",
      },
    };

    return paddingMap[iconPos][size];
  }};

  gap: ${({ size }) => {
    const gapMap = {
      Large: "12px",
      Medium: "10px",
      Small: "8px",
      XSmall: "2px",
    };

    return gapMap[size];
  }};
`;
